import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './invoicing.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider } from '../../../components/AppToolbar';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect } from '../../modules/DateRangeSelect';
import { QUOTE_RANGES, rangeCode, rangeDays, type QuoteDateFilter } from '../quotes/types';
import {
  apiErrorMessage, deleteInvoices, fetchValidation, previewSavedInvoice, reviewInvoices, rollbackInvoices,
} from './api';
import InvoiceValidationDetailPage from './InvoiceValidationDetailPage';
import type { InvoiceValidationRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Invoicing ▸ Invoice Validation (nav `acct-invoice-validation`).
// Port of legacy ALPACA `Invoicing_ListView_Review` (Model.xafml :26165) and its
// History `Invoicing_ListView_Review_History` (:26220), InvoicingViewController.
//
// LIST — invoices Pending Review, sorted by the client's credit rating then
// InvoiceID descending, division-scoped (the criteria live in
// dbo.AI_Invoicing_ValidationList_SP). Toolbar, legacy order:
//   Review · History · Preview · Edit · Delete · Refresh · Export to
// HISTORY — invoices Pending Delivery or Delivered, with the Date Filter on
// Date Invoiced (1M first) and Roll Back (reason required).
//
// Review: an invoice with an Email is mailed and goes straight to Delivered;
// any other goes to Pending Delivery (the server decides, InvoiceValidationController).
// AUDIT. FormName "Invoice Validation".
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Invoice Validation';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// G5 Invoicing_ListView_Review, its columns in its order (no Division).
const COLUMNS: AppColumn<InvoiceValidationRow>[] = [
  { field: 'invoiceID',     header: 'InvoiceID',     width: 130 },
  { field: 'jobID',         header: 'JobID',         width: 150 },
  { field: 'client',        header: 'Client',        width: 210 },
  { field: 'amount',        header: 'Amount',        width: 110, align: 'right', type: 'number',
    render: (r) => (r.amount === null ? '' : money.format(r.amount)) },
  { field: 'projectID',     header: 'ProjectID',     width: 110 },
  { field: 'projectName',   header: 'ProjectName',   width: 170 },
  { field: 'accountNumber', header: 'AccountNumber', width: 120 },
  { field: 'status',        header: 'Status',        width: 140, type: 'status' },
  { field: 'dateInvoiced',  header: 'DateInvoiced',  width: 120, type: 'date' },
  { field: 'invoicedBy',    header: 'InvoicedBy',    width: 140 },
  { field: 'reviewedBy',    header: 'ReviewedBy',    width: 140 },
  { field: 'dateReviewed',  header: 'DateReviewed',  width: 120, type: 'date' },
];

type Screen = { kind: 'list' } | { kind: 'detail'; id: string; key: number };

export default function InvoiceValidationPage() {
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  const runDelete = useDeleteFlow();
  const perm = useNavActions('acct-invoice-validation');

  const [history, setHistory] = useState(false);
  const [dateFilter, setDateFilter] = useState<QuoteDateFilter>('1M');
  const [rows, setRows] = useState<InvoiceValidationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });

  const load = useCallback(async (inHistory = history, filter = dateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchValidation(inHistory, filter));
      setSelected(new Set());
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'Failed to load the invoices.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [history, dateFilter, notify]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => COLUMNS.some((c) => {
      const v = r[c.field as keyof InvoiceValidationRow];
      return typeof v === 'string' && v.toLowerCase().includes(q);
    }));
  }, [rows, search]);

  const ids = [...selected].map(String);
  const one = ids.length === 1 ? ids[0] : undefined;

  /** Legacy Review_Execute. */
  async function review() {
    if (ids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    const picked = rows.filter((r) => ids.includes(r.oid));
    const mailed = picked.filter((r) => r.email?.trim()).length;
    const ok = await confirm({
      title: 'Review',
      message: mailed > 0
        ? `Review ${picked.length} invoice(s)? ${mailed} will be e-mailed to the client and marked Delivered; the rest go to Invoice Delivery.`
        : `Review ${picked.length} invoice(s)? They go to Invoice Delivery.`,
    });
    if (!ok) return;
    try {
      const res = await reviewInvoices(ids);
      if (res.failed > 0) notify.error(res.message);
      else notify.success(res.message);
      void load();
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'The invoices could not be reviewed.');
      void load();
    }
  }

  /** Legacy DeleteAction_Executing — the tests go back to the Invoice Queue. */
  async function remove() {
    if (ids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    await runDelete({
      formName: FORM_NAME,
      message: `Delete ${ids.length} invoice(s)? Their tests go back to the Invoice Queue.`,
      delete: () => deleteInvoices(ids),
      onDeleted: () => { void load(); },
      onError: (e) => { const m = apiErrorMessage(e); if (m) notify.error(m); },
    });
  }

  /** Legacy Rollback_Execute / RollBackReason_Accepting (History only). */
  async function rollback() {
    if (ids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    const reason = await prompt({
      title: 'Roll Back',
      message: 'Rollback Reason',
      required: true,
    });
    if (reason === null) return;
    if (!reason.trim()) { notify.warning('Please enter the Rollback Reason'); return; }
    try {
      notify.success(await rollbackInvoices(ids, reason.trim()));
      void load();
    } catch (e) {
      notify.error(apiErrorMessage(e) ?? 'The invoices could not be rolled back.');
    }
  }

  async function preview() {
    if (!one) return;
    try { await previewSavedInvoice(one); }
    catch (e) { notify.error(apiErrorMessage(e) ?? 'The invoice could not be previewed.'); }
  }

  function showHistory(on: boolean) {
    setHistory(on);
    setSearch('');
    void load(on, dateFilter);
  }

  useHotkeys([
    { combo: 'alt+v', label: 'Review', group: FORM_NAME, run: () => void review(),
      disabled: history || !perm.write || screen.kind !== 'list' || selected.size === 0, order: 1 },
    { combo: 'alt+b', label: 'Roll Back', group: FORM_NAME, run: () => void rollback(),
      disabled: !history || !perm.write || screen.kind !== 'list' || selected.size === 0, order: 2 },
  ]);

  // MAC: Review / Roll Back / Edit need Write, Delete needs Delete — hidden, never greyed.
  const canWrite = perm.write;

  if (screen.kind === 'detail') {
    return (
      <InvoiceValidationDetailPage
        key={screen.key}
        id={screen.id}
        ids={shown.map((r) => r.oid)}
        readOnly={history || !canWrite}
        onNavigate={(id) => setScreen({ kind: 'detail', id, key: Date.now() })}
        onClose={() => { setScreen({ kind: 'list' }); void load(); }}
      />
    );
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="acct-invoice-validation" loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />
      {history && <div className="inv-hist-note">History — reviewed invoices (Pending Delivery or Delivered). Roll Back returns one to Invoice Validation.</div>}
      <AppToolbar
        left={history ? (
          <>
            <button type="button" className="ca-btn" onClick={() => showHistory(false)}>
              <i className="ti ti-arrow-left" aria-hidden="true" /> Invoice Validation
            </button>
            {canWrite && (
              <button type="button" className="ca-btn" onClick={() => void rollback()}
                      disabled={selected.size === 0} title={`Roll Back (${formatCombo('alt+b')})`}>
                <i className="ti ti-arrow-back-up" aria-hidden="true" /> Roll Back
              </button>
            )}
          </>
        ) : (
          <>
            {canWrite && (
              <button type="button" className="ca-btn" onClick={() => void review()}
                      disabled={selected.size === 0} title={`Review (${formatCombo('alt+v')})`}>
                <i className="ti ti-circle-check tb-i-green" aria-hidden="true" /> Review
              </button>
            )}
            <button type="button" className="ca-btn" onClick={() => showHistory(true)}>
              <i className="ti ti-history" aria-hidden="true" /> History
            </button>
          </>
        )}
        onPreview={() => void preview()}
        previewDisabled={!one}
        onEdit={canWrite ? () => { if (one) setScreen({ kind: 'detail', id: one, key: Date.now() }); } : undefined}
        editDisabled={!one}
        onDelete={history || !perm.delete ? undefined : () => void remove()}
        deleteDisabled={selected.size === 0}
        beforeRefresh={history ? (
          <>
            <ToolbarDivider />
            <DateRangeSelect
              variant="dropdown" label="Date Filter" ranges={QUOTE_RANGES}
              value={rangeDays(dateFilter)}
              onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(true, c); }}
            />
          </>
        ) : undefined}
        onRefresh={() => void load()}
        refreshing={loading}
        exportData={{ filename: history ? 'invoice-review-history' : 'invoice-validation', ...gridExportMatrix(shown, COLUMNS) }}
      />
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<InvoiceValidationRow>
        rows={shown}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        // G5 freezes selection, InvoiceID, JobID and Client (:3730-3760).
        stickyColumns={3}
        onRowDoubleClick={(r) => setScreen({ kind: 'detail', id: r.oid, key: Date.now() })}
        emptyText={search ? 'No results match your search.'
          : history ? 'No reviewed invoices in this period.' : 'No invoices are waiting for validation.'}
      />
    </div>
  );
}
